import { MathUtils, Object3D, Vector2 } from 'three';

import { lisa } from './Lisa.js';
import { DEVICE, LAYERS } from './constants.js';
import ScreenRaycaster from './utils/Raycaster.js';
import { applyInstances, applyModifiers } from './utils/modifiers.js';
import { globalUniforms } from './utils/uniforms.js';

export default class Character extends Object3D {
	#keys = [
		['left', new Vector2(1, 0), 'lerp'],
		['right', new Vector2(-1, 0), 'lerp'],
		['up', new Vector2(0, 1), 'lerp'],
		['down', new Vector2(0, -1), 'lerp'],

		['blink', null, 'blink'],

		['smile', new Vector2(0.5, 0), 'periodic'],
		['frown', new Vector2(1.3, 0.2), 'periodic'],
	];

	#lerpedMouse = new Vector2();
	#fastMouse = new Vector2();
	#slowMouse = new Vector2();
	#mouseDirection = new Vector2();
	#mouseSecondDirection = new Vector2();

	constructor() {
		super();
		this.model = lisa.assets.model.scene;
		this.atlas = this.#fetchMaterials(this.model);

		this.lisa = this.model.getObjectByName('Lisa');

		lisa.state.register(this);

		applyInstances(this.lisa);
		applyModifiers(this.lisa);

		this.add(this.lisa);

		this.lights = [];

		this.lisa.traverse((child) => {
			if (child.material?.name === 'Screen') {
				this.screen = child;
				child.layers.enable(LAYERS.OUTSCREEN);
				child.layers.enable(LAYERS.SCREEN);
			} else if (child.material?.name.slice(0, 6) === 'Lights') {
				this.lights.push(child);
			} else {
				child.layers.enable(LAYERS.OUTSCREEN);
			}
		});

		this.head = this.lisa.getObjectByName('Head');

		this.#createRayCaster(this.lisa);
		this.#getArmature();
		this.#exposeAdditiveRotations();
	}

	#fetchMaterials(model) {
		const atlas = new Map();
		const mapsObject = model.getObjectByName('Maps');
		mapsObject.traverse((child) => {
			if (child.material) {
				const mat = child.material;
				if (mat.map) {
					atlas.set(mat.name + 'Map', child.material.map);
				}
				if (mat.roughnessMap) {
					atlas.set(mat.name + 'ARMMap', child.material.roughnessMap);
				}
				if (mat.normalMap) {
					atlas.set(mat.name + 'NormalMap', child.material.normalMap);
				}
			}
		});
		return atlas;
	}

	#createRayCaster(model) {
		this.raycaster = new ScreenRaycaster(model.getObjectByName('Raycaster'));
	}

	#getArmature = () => {
		this.fullArmature = this.lisa.getObjectByName('Armature');
		// lisa.scene.add(new SkeletonHelper(this.fullArmature));
		this.armature = {
			neck2: this.fullArmature.getObjectByName('neck2'),
			neck1: this.fullArmature.getObjectByName('neck1'),
			torso: this.fullArmature.getObjectByName('torso'),
			shoulderL: this.fullArmature.getObjectByName('shoulderL'),
			shoulderR: this.fullArmature.getObjectByName('shoulderR'),
			cableLStart: this.fullArmature.getObjectByName('cable_L_start'),
			cableLEnd: this.fullArmature.getObjectByName('cable_L_end'),
		};
		this.armatureRotations = {
			neck2: this.armature.neck2.rotation.clone(),
			neck1: this.armature.neck1.rotation.clone(),
			torso: this.armature.torso.rotation.clone(),
			shoulderL: this.armature.shoulderL.rotation.clone(),
			shoulderR: this.armature.shoulderR.rotation.clone(),
			cableLStart: this.armature.cableLStart.rotation.clone(),
			cableLEnd: this.armature.cableLEnd.rotation.clone(),
		};
	};

	#exposeAdditiveRotations = () => {
		this.additiveRotations = {
			neck2: {
				x: 0,
				z: 0,
			},
			neck1: {
				x: 0,
				y: 0,
			},
			torso: {
				x: 0,
			},
		};
	};

	onTick = ({ et, dt }) => {
		if (DEVICE.MOTION) {
			this.#lerpedMouse.lerp(globalUniforms.uMotion.value, 0.02);
			this.#fastMouse.lerp(globalUniforms.uMotion.value, 0.035);
			this.#slowMouse.lerp(lisa.mouse, 0.005);

			this.#mouseDirection.subVectors(this.#fastMouse, this.#lerpedMouse);
			this.#mouseSecondDirection.subVectors(this.#lerpedMouse, this.#slowMouse);
		} else {
			this.#lerpedMouse.lerp(lisa.mouse, 0.04);
			this.#fastMouse.lerp(lisa.mouse, 0.1);
			this.#slowMouse.lerp(lisa.mouse, 0.02);

			this.#mouseDirection.subVectors(this.#fastMouse, this.#lerpedMouse);
			this.#mouseSecondDirection.subVectors(this.#lerpedMouse, this.#slowMouse);
		}

        const X_DECAY_FACTOR = 0.33

		/* neck following mouse */
		this.armature.neck1.rotation.z = MathUtils.lerp(
			this.armatureRotations.neck1.z + 0.08,
			this.armatureRotations.neck1.z - 0.08,
			MathUtils.smoothstep(this.#lerpedMouse.x - lisa.xDecay * X_DECAY_FACTOR, -1, 1),
		);
		this.armature.neck1.rotation.x = MathUtils.lerp(
			this.armatureRotations.neck1.x + 0.1,
			this.armatureRotations.neck1.x + -0.1,
			MathUtils.smoothstep(this.#lerpedMouse.y, -1, 1),
		);

		this.armature.neck1.rotation.y = MathUtils.lerp(
			this.armatureRotations.neck1.y - 0.3,
			this.armatureRotations.neck1.y + 0.3,
			MathUtils.smoothstep(this.#lerpedMouse.x - lisa.xDecay * X_DECAY_FACTOR, -1, 1),
		);
		// this.armature.neck1.rotation.z += this.additiveRotations.neck.z;

		this.armature.torso.rotation.z = MathUtils.lerp(
			this.armatureRotations.torso.z + 0.01,
			this.armatureRotations.torso.z - 0.01,
			MathUtils.smoothstep(this.#lerpedMouse.x - lisa.xDecay * X_DECAY_FACTOR, -1, 1),
		);
		this.armature.torso.rotation.x = MathUtils.lerp(
			this.armatureRotations.torso.x + 0.05,
			this.armatureRotations.torso.x + -0.05,
			MathUtils.smoothstep(this.#lerpedMouse.y, -1, 1),
		);
		this.armature.torso.rotation.y = MathUtils.lerp(
			this.armatureRotations.torso.y - 0.1,
			this.armatureRotations.torso.y + 0.1,
			MathUtils.smoothstep(this.#lerpedMouse.x - lisa.xDecay * X_DECAY_FACTOR, -1, 1),
		);
		// this.armature.neck.rotation.y = this.armatureRotations.neck.y + Math.sin(et);

		/* Cables swinging */
		this.armature.cableLStart.rotation.z = MathUtils.lerp(
			this.armatureRotations.cableLStart.z + 0.5,
			this.armatureRotations.cableLStart.z + -0.5,

			MathUtils.smoothstep(this.#mouseDirection.x, -1, 1),
		);
		this.armature.cableLEnd.rotation.z = MathUtils.lerp(
			this.armatureRotations.cableLEnd.z + 0.5,
			this.armatureRotations.cableLEnd.z + -0.5,
			MathUtils.smoothstep(this.#mouseSecondDirection.x, -1, 1),
		);

		/* Breathing */
		this.armature.shoulderL.rotation.y = this.armatureRotations.shoulderL.y + 0.02 * Math.sin(et * 1.2);
		this.armature.shoulderR.rotation.y = this.armatureRotations.shoulderR.y - 0.02 * Math.sin(et * 1.2);

		/* Head slight circular movements*/
		this.armature.neck2.rotation.x = this.armatureRotations.neck2.x + 0.05 * Math.sin(et * 0.51);
		this.armature.neck2.rotation.z = this.armatureRotations.neck2.z + 0.04 * Math.sin(et * 0.23);
		this.armature.neck2.rotation.y = this.armatureRotations.neck2.y;

		/* Animations */

		this.armature.neck2.rotation.x += this.additiveRotations.neck2.x;
		this.armature.neck2.rotation.z += this.additiveRotations.neck2.z;
		this.armature.neck1.rotation.y += this.additiveRotations.neck1.y;
		this.armature.neck1.rotation.x += this.additiveRotations.neck1.x;
		this.armature.torso.rotation.x += this.additiveRotations.torso.x;
	};
}
